/**
 * Client SMS / email template bodies, mirrored from the backend
 * (McOpsActionsService::SMS_TEMPLATES / EMAIL_TEMPLATES) so the coordinator can
 * preview and EDIT the message before it goes out. Placeholders are filled from
 * the job + the signed-in coordinator; the edited text is what actually sends.
 */

const SMS_BODIES = {
  "Move date reminder":
    "Hi {client}, this is {coordinator} from Roadway Moving. A friendly reminder that your move is scheduled for {moveDate}. Reply here or call us with any questions — happy to help!",
  "Introduction message":
    "Hi {client}, I'm {coordinator}, your move coordinator at Roadway Moving. I'll be your point of contact from now through move day. Feel free to text me here anytime with questions.",
  "Confirmation request":
    "Hi {client}, this is {coordinator} from Roadway Moving. I'd like to confirm the details for your move on {moveDate}. When's a good time for a quick call? Thank you!",
  "Missed you — please call back":
    "Hi {client}, this is {coordinator} from Roadway Moving. I tried reaching you about your upcoming move but couldn't connect. Please call or text me back whenever it's convenient. Thanks!",
};

const EMAIL_BODIES = {
  "Introduction email": {
    subject: "Your Roadway Moving coordinator",
    body: "Hi {client},\n\nI'm {coordinator}, your move coordinator at Roadway Moving. I'll be your point of contact from now through your move day{moveDateClause}. If you have any questions about logistics, paperwork, or what to expect, just reply to this email or give us a call anytime.\n\nLooking forward to working with you.\n\nBest,\n{coordinator}\nRoadway Moving",
  },
  "Move date confirmation": {
    subject: "Confirming your move details",
    body: "Hi {client},\n\nI'd like to confirm the details for your upcoming move{moveDateClause}. Please review everything on your end and let me know if anything has changed. If it all looks correct, just reply to confirm.\n\nThank you,\n{coordinator}\nRoadway Moving",
  },
  "Documents needed": {
    subject: "A few documents for your move",
    body: "Hi {client},\n\nTo keep your move on schedule, we still need a few items from you — such as a Certificate of Insurance if your building requires one, and your signed paperwork. Reply to this email and I'll walk you through exactly what's needed.\n\nThanks,\n{coordinator}\nRoadway Moving",
  },
  "Missed call follow-up": {
    subject: "Tried reaching you about your move",
    body: "Hi {client},\n\nI tried giving you a call about your upcoming move{moveDateClause} but couldn't reach you. Whenever you have a moment, please call or reply here and we'll pick up where we left off.\n\nBest,\n{coordinator}\nRoadway Moving",
  },
};

const prettyDate = (raw) => {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  } catch (e) {
    return String(raw);
  }
};

/** Built-in templates as flat records, so the manager can list them too. */
export const DEFAULT_TEMPLATES = [
  ...Object.keys(SMS_BODIES).map((name) => ({
    channel: "text",
    name,
    subject: "",
    body: SMS_BODIES[name],
    builtin: true,
  })),
  ...Object.keys(EMAIL_BODIES).map((name) => ({
    channel: "email",
    name,
    subject: EMAIL_BODIES[name].subject,
    body: EMAIL_BODIES[name].body,
    builtin: true,
  })),
];

const fill = (text, vars) =>
  String(text || "")
    .replace(/\{client\}/g, vars.client || "there")
    .replace(/\{coordinator\}/g, vars.coordinator || "your Roadway Moving coordinator")
    .replace(/\{moveDate\}/g, vars.moveDate ? prettyDate(vars.moveDate) : "the scheduled date")
    .replace(/\{moveDateClause\}/g, vars.moveDate ? " on " + prettyDate(vars.moveDate) : "");

/** Built-in template names per channel (custom ones are appended at runtime). */
export const builtinNames = (channel) =>
  channel === "email" ? Object.keys(EMAIL_BODIES) : Object.keys(SMS_BODIES);

/** Merge built-in + custom template names for a channel (deduped, custom wins). */
export const templateNames = (channel, customTemplates = []) =>
  Array.from(
    new Set([
      ...builtinNames(channel),
      ...customTemplates.filter((t) => t.channel === channel).map((t) => t.name),
    ]),
  );

/**
 * Resolve a template into an editable draft. A shared custom template (same
 * channel + name) wins over the built-in one.
 * @returns {{subject: string, body: string}} — subject is "" for SMS.
 */
export const resolveTemplate = (channel, name, vars, customTemplates = []) => {
  const custom = customTemplates.find((t) => t.channel === channel && t.name === name);
  if (custom) {
    return {
      subject: channel === "email" ? fill(custom.subject || "", vars) : "",
      body: fill(custom.body || "", vars),
    };
  }
  if (channel === "text") {
    return { subject: "", body: fill(SMS_BODIES[name] || "", vars) };
  }
  const tpl = EMAIL_BODIES[name] || { subject: "", body: "" };
  return { subject: fill(tpl.subject, vars), body: fill(tpl.body, vars) };
};
